import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { ThreeMFLoader } from 'three/examples/jsm/loaders/3MFLoader.js';
import { Model3DStats } from './types';

export function calculateGeometryStats(geometry: THREE.BufferGeometry): {
  volumeMm3: number;
  surfaceAreaMm2: number;
  triangles: number;
} {
  let volume = 0;
  let surfaceArea = 0;

  const pos = geometry.attributes.position;
  if (!pos) {
    return { volumeMm3: 0, surfaceAreaMm2: 0, triangles: 0 };
  }

  const index = geometry.index;
  const p1 = new THREE.Vector3();
  const p2 = new THREE.Vector3();
  const p3 = new THREE.Vector3();
  const v1 = new THREE.Vector3();
  const v2 = new THREE.Vector3();
  const cross = new THREE.Vector3();

  const processTriangle = (i1: number, i2: number, i3: number) => {
    p1.fromBufferAttribute(pos, i1);
    p2.fromBufferAttribute(pos, i2);
    p3.fromBufferAttribute(pos, i3);

    // Tetrahedron signed volume: (p1 . (p2 x p3)) / 6
    cross.crossVectors(p2, p3);
    volume += p1.dot(cross) / 6.0;

    // Triangle surface area: 0.5 * ||(p2 - p1) x (p3 - p1)||
    v1.subVectors(p2, p1);
    v2.subVectors(p3, p1);
    surfaceArea += v1.cross(v2).length() * 0.5;
  };

  const triangleCount = index ? index.count / 3 : pos.count / 3;

  if (index) {
    for (let i = 0; i < index.count; i += 3) {
      processTriangle(index.getX(i), index.getX(i + 1), index.getX(i + 2));
    }
  } else {
    for (let i = 0; i < pos.count; i += 3) {
      processTriangle(i, i + 1, i + 2);
    }
  }

  return {
    volumeMm3: Math.abs(volume),
    surfaceAreaMm2: surfaceArea,
    triangles: Math.round(triangleCount),
  };
}

export async function parseModelFile(
  file: File,
  densityGPerCm3 = 1.24,
  infillPercent = 20
): Promise<{ object3D: THREE.Object3D; stats: Model3DStats }> {
  const extension = file.name.split('.').pop()?.toLowerCase();
  let rootObject: THREE.Object3D;

  if (extension === 'stl') {
    const buffer = await file.arrayBuffer();
    const stlLoader = new STLLoader();
    const geometry = stlLoader.parse(buffer);
    geometry.computeVertexNormals();
    const material = new THREE.MeshStandardMaterial({
      color: 0x9333ea,
      roughness: 0.35,
      metalness: 0.1,
    });
    rootObject = new THREE.Mesh(geometry, material);
  } else if (extension === 'obj') {
    const text = await file.text();
    const objLoader = new OBJLoader();
    rootObject = objLoader.parse(text);
  } else if (extension === '3mf') {
    const buffer = await file.arrayBuffer();
    const threeMfLoader = new ThreeMFLoader();
    rootObject = threeMfLoader.parse(buffer);
  } else {
    throw new Error('Formato no soportado. Debe ser .stl, .obj o .3mf');
  }

  // Calculate volume, area and bounding box
  let totalVolumeMm3 = 0;
  let totalSurfaceAreaMm2 = 0;
  let totalTriangles = 0;

  rootObject.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      const mesh = child as THREE.Mesh;
      if (mesh.geometry) {
        const s = calculateGeometryStats(mesh.geometry);
        totalVolumeMm3 += s.volumeMm3;
        totalSurfaceAreaMm2 += s.surfaceAreaMm2;
        totalTriangles += s.triangles;
      }
    }
  });

  const bbox = new THREE.Box3().setFromObject(rootObject);
  const size = new THREE.Vector3();
  bbox.getSize(size);

  const volumeCm3 = totalVolumeMm3 / 1000;
  const surfaceAreaCm2 = totalSurfaceAreaMm2 / 100;

  // Formula estimation for infill + walls
  // Standard shell + infill ratio approximation
  const infillFactor = 0.20 + 0.80 * (infillPercent / 100);
  const estimatedWeightGrams = volumeCm3 * densityGPerCm3 * infillFactor;

  const stats: Model3DStats = {
    fileName: file.name,
    fileSizeBytes: file.size,
    fileType: extension as 'stl' | 'obj' | '3mf',
    dimensions: {
      x: Math.round(size.x * 10) / 10,
      y: Math.round(size.y * 10) / 10,
      z: Math.round(size.z * 10) / 10,
    },
    volumeCm3: Math.round(volumeCm3 * 100) / 100,
    surfaceAreaCm2: Math.round(surfaceAreaCm2 * 100) / 100,
    estimatedWeightGrams: Math.round(estimatedWeightGrams * 10) / 10,
    triangleCount: totalTriangles,
  };

  return { object3D: rootObject, stats };
}
